"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowDown01Icon, Cancel01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import * as React from "react";
import { ResponsivePopover } from "../../ui/responsive-popover";
import { Typography } from "../../ui/typography";
import { Counter } from "@/components/shared/Counter";
import { useSearchParamsWithPath } from "@/hooks/useSearchParamsWithPath";
import { useSearchParams } from "next/navigation";

interface CapacityFilterProps {
  className?: string;
}

export function CapacityFilter({ className }: CapacityFilterProps) {
  const [open, setOpen] = React.useState(false);
  const [capacity, setCapacity] = React.useState(1);
  const { updateParam, removeParam } = useSearchParamsWithPath();
  const searchParams = useSearchParams();

  // Initialize and sync capacity from URL
  React.useEffect(() => {
    const capacityParam = searchParams.get("capacity");
    if (capacityParam) {
      const parsed = parseInt(capacityParam, 10);
      if (!isNaN(parsed) && parsed > 0) {
        setCapacity(parsed);
      }
    } else {
      setCapacity(1);
    }
  }, [searchParams]);

  const handleCapacityChange = (newValue: number) => {
    setCapacity(newValue);
  };

  const handleSubmit = () => {
    if (capacity > 0) {
      if (capacity === 1) {
        // Remove param if default value
        removeParam("capacity");
      } else {
        updateParam("capacity", capacity.toString());
      }
    }
    setOpen(false);
  };

  const handleDelete = () => {
    setCapacity(1);
    removeParam("capacity");
  };

  const currentCapacity = searchParams.get("capacity");
  const displayCapacity = currentCapacity
    ? parseInt(currentCapacity, 10)
    : null;

  return (
    <ResponsivePopover
      open={open}
      onOpenChange={setOpen}
      className="md:w-[424px]"
      direction="bottom"
      sideOffset={16}
      trigger={
        <Button
          id="capacity"
          variant="pill"
          className={cn(
            "transition-colors duration-500",
            displayCapacity && displayCapacity > 1
              ? "border-primary text-primary"
              : "",
            className
          )}
        >
          {displayCapacity && displayCapacity > 1 ? (
            <Typography variant="captionRegular">
              {displayCapacity} نفر
            </Typography>
          ) : (
            <Typography variant="captionRegular">تعداد نفرات</Typography>
          )}
          <HugeiconsIcon icon={ArrowDown01Icon} />
        </Button>
      }
    >
      <Typography
        variant="bodySMedium"
        className="py-4 border-b text-center text-foreground mb-4 md:hidden block"
      >
        تعداد نفرات
      </Typography>
      <Counter
        label="تعداد نفرات"
        onChange={handleCapacityChange}
        value={capacity}
        min={1}
      />
      <div className="flex justify-between gap-10 items-center mt-6 md:mt-0 md:border-t px-2 md:p-4">
        <Button variant="ghost" onClick={handleDelete} className="p-1 md:p-1">
          <HugeiconsIcon
            className="size-[18px] hidden md:block"
            icon={Cancel01Icon}
          />
          <Typography variant="bodySRegular">حذف</Typography>
        </Button>
        <Button
          onClick={handleSubmit}
          className="flex-1 md:flex-none h-[50px] md:h-[35px] rounded-lg font-normal"
        >
          <Typography variant="captionMedium" className="md:hidden">
            اعمال تغییرات
          </Typography>
          <Typography
            variant="captionRegular"
            className="hidden md:inline px-1"
          >
            اعمال
          </Typography>
        </Button>
      </div>
    </ResponsivePopover>
  );
}
